import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plus, ShoppingCart, Edit, Trash2, ArrowRight, Send, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_MAP = {
  bozza:      { label: 'Bozza',      color: 'bg-slate-100 text-slate-700 border-slate-300' },
  inviato:    { label: 'Inviato',    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  confermato: { label: 'Confermato', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  evaso:      { label: 'Evaso',      color: 'bg-green-50 text-green-700 border-green-200' },
  annullato:  { label: 'Annullato',  color: 'bg-red-50 text-red-700 border-red-200' },
};

const emptyForm = {
  date: new Date().toISOString().split('T')[0], expected_delivery: '',
  supplier_id: '', site_id: '', payment_terms: '', notes: '', lines: [],
};

export default function OrdersPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'qhse', 'capo_cantiere');
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [sites, setSites] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== '__all__') params.status = filterStatus;
      const [oRes, sRes, siRes, invRes] = await Promise.all([
        api.get('/orders', { params }),
        api.get('/suppliers/names'),
        api.get('/sites'),
        api.get('/inventory'),
      ]);
      setItems(oRes.data);
      setSuppliers(sRes.data);
      setSites(siRes.data);
      setInventoryItems(Array.isArray(invRes.data) ? invRes.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); setPage(1); }, [load]);

  const fmt = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

  const openCreate = () => { setEditItem(null); setForm({ ...emptyForm, lines: [] }); setShowDialog(true); };
  const openEdit = (o) => {
    setEditItem(o);
    setForm({ date: o.date, expected_delivery: o.expected_delivery || '', supplier_id: o.supplier_id || '', site_id: o.site_id || '', payment_terms: o.payment_terms || '', notes: o.notes || '', lines: (o.lines || []).map(l => ({ ...l })) });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editItem) { await api.put(`/orders/${editItem.id}`, form); }
      else { await api.post('/orders', form); }
      setShowDialog(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo ordine?')) return;
    try { await api.delete(`/orders/${id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleStatus = async (id, status) => {
    try { await api.post(`/orders/${id}/status`, { status }); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { item_id: '', description: '', quantity: 1, unit: 'pz', unit_price: 0 }] }));
  const updateLine = (idx, field, val) => setForm(f => { const l = [...f.lines]; l[idx] = { ...l[idx], [field]: val }; return { ...f, lines: l }; });
  const selectItem = (idx, itemId) => {
    const inv = inventoryItems.find(i => i.id === itemId);
    if (inv) {
      setForm(f => {
        const l = [...f.lines];
        l[idx] = { ...l[idx], item_id: itemId, description: inv.name, unit: inv.unit || 'pz' };
        return { ...f, lines: l };
      });
    }
  };
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const formTotal = form.lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);

  const paged = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(items.length / PAGE_SIZE);
  const totalValue = items.reduce((s, i) => s + (i.total_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Ordini a Fornitore</h1>
          <p className="text-sm text-slate-500 mt-1">Gestione ordini di acquisto materiali e servizi</p>
        </div>
        {canManage && <Button onClick={openCreate} data-testid="add-order-btn"><Plus size={16} className="mr-1" /> Nuovo Ordine</Button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Totale Ordini</p><p className="text-xl font-bold text-slate-700">{items.length}</p></CardContent></Card>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Valore Totale</p><p className="text-xl font-bold text-slate-700">{fmt(totalValue)}</p></CardContent></Card>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Da Evadere</p><p className="text-xl font-bold text-amber-600">{items.filter(i => i.status === 'confermato').length}</p></CardContent></Card>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Evasi</p><p className="text-xl font-bold text-green-600">{items.filter(i => i.status === 'evaso').length}</p></CardContent></Card>
      </div>

      <Select value={filterStatus} onValueChange={setFilterStatus}>
        <SelectTrigger className="w-48" data-testid="order-status-filter"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Tutti gli stati</SelectItem>
          {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>N. Ordine</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Fornitore</TableHead>
                <TableHead>Cantiere</TableHead>
                <TableHead className="text-right">Importo</TableHead>
                <TableHead>Consegna</TableHead>
                <TableHead>Stato</TableHead>
                {canManage && <TableHead className="w-32">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Nessun ordine</TableCell></TableRow>
              ) : paged.map(o => (
                <TableRow key={o.id} className="hover:bg-slate-50/50" data-testid={`order-row-${o.id}`}>
                  <TableCell className="font-mono text-sm font-medium">{o.order_number}</TableCell>
                  <TableCell className="text-sm">{o.date}</TableCell>
                  <TableCell className="text-sm max-w-[180px] truncate">{o.supplier_name || '-'}</TableCell>
                  <TableCell className="text-sm max-w-[150px] truncate">{o.site_name || '-'}</TableCell>
                  <TableCell className="text-sm text-right font-medium">{fmt(o.total_amount || 0)}</TableCell>
                  <TableCell className="text-sm">{o.expected_delivery || '-'}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_MAP[o.status]?.color || ''}`}>{STATUS_MAP[o.status]?.label || o.status}</Badge></TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(o)}><Edit size={13} /></Button>
                        {o.status === 'bozza' && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => handleStatus(o.id, 'inviato')} title="Invia"><Send size={13} /></Button>}
                        {o.status === 'inviato' && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-600" onClick={() => handleStatus(o.id, 'confermato')} title="Conferma"><Check size={13} /></Button>}
                        {o.status === 'confermato' && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={() => handleStatus(o.id, 'evaso')} title="Evadi"><ArrowRight size={13} /></Button>}
                        {isRole('admin') && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(o.id)}><Trash2 size={13} /></Button>}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-xs text-slate-500">{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} di {items.length}</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
            <span className="text-xs text-slate-600 px-2">Pag. {page}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader><DialogTitle>{editItem ? 'Modifica Ordine' : 'Nuovo Ordine'}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Data *</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="order-date-input" /></div>
              <div className="space-y-1.5"><Label>Consegna Prevista</Label><Input type="date" value={form.expected_delivery} onChange={e => setForm({ ...form, expected_delivery: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Fornitore *</Label>
                <Select value={form.supplier_id} onValueChange={v => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger data-testid="order-supplier-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cantiere</Label>
                <Select value={form.site_id} onValueChange={v => setForm({ ...form, site_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Cond. Pagamento</Label><Input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Note</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <div className="mt-4 border rounded-lg p-3 bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Righe Ordine</Label>
                <Button variant="outline" size="sm" onClick={addLine} data-testid="order-add-line"><Plus size={12} className="mr-1" /> Riga</Button>
              </div>
              {form.lines.length === 0 ? (
                <p className="text-xs text-slate-400 py-3 text-center">Nessuna riga</p>
              ) : form.lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-3">
                    <Select value={l.item_id || 'manual'} onValueChange={v => { if (v === 'manual') { updateLine(idx, 'item_id', ''); } else { selectItem(idx, v); } }}>
                      <SelectTrigger className="text-xs h-9"><SelectValue placeholder="Articolo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">-- Manuale --</SelectItem>
                        {inventoryItems.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.code || i.id.slice(0,6)})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input placeholder="Descrizione" value={l.description} onChange={e => updateLine(idx, 'description', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Qty" value={l.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-1"><Input placeholder="U.M." value={l.unit} onChange={e => updateLine(idx, 'unit', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-2"><Input type="number" step="0.01" placeholder="Prezzo" value={l.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)} className="text-sm" /></div>
                  <div className="col-span-1 text-right text-xs font-medium pt-2">{fmt((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0))}</div>
                  <div className="col-span-1"><Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => removeLine(idx)}><X size={14} /></Button></div>
                </div>
              ))}
              {form.lines.length > 0 && (
                <div className="text-right font-bold text-sm border-t pt-2 mt-2">Totale: {fmt(formTotal)}</div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="order-save-btn">{editItem ? 'Salva' : 'Crea Ordine'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
